import type * as Highcharts from 'highcharts';
import dayjs from 'dayjs';
import { colors, plotLines } from '../../constants';

type SeriesValue = number | null | undefined | Highcharts.PointOptionsObject;

export interface TimeSeriesChartData {
  times: string[];
  values: Record<string, SeriesValue[] | undefined>;
  units?: Record<string, string>;
  title: string;
  xTitle: string;
  rain_dates?: string[];
  cleaning_dates?: string[];
}

export interface TimeSeriesAxisOptions {
  ymin?: number;
  ymax?: number;
  // Alignment with a stacked sibling chart (e.g. the cleaning schedules heatmap):
  // - datetimeAxis: use a real time axis (same min/max/tickInterval => identical ticks)
  // - marginLeft/marginRight: fix the plot area so a date lands at the same pixel on both charts
  datetimeAxis?: boolean;
  marginLeft?: number;
  marginRight?: number;
  tickInterval?: number;
}

const PLOT_LINES: Record<string, { label?: string; color: string }> = plotLines;
const DAY_MS = 24 * 3600 * 1000;
const CLEANING_COLORS: Record<number, string> = { 1: '#FF6B35', 2: '#C55824', 3: '#808080' };

const hasPlotLine = (key: string) => Object.prototype.hasOwnProperty.call(PLOT_LINES, key);

const pad2 = (value: number) => ('0' + value).slice(-2);

const formatDay = (x: string) => {
  const d = new Date(Date.parse(x));
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
};

const seriesName = (key: string) => {
  if (hasPlotLine(key)) return PLOT_LINES[key].label;
  if (key.includes('_production')) return 'Production using ' + key.substring(0, key.lastIndexOf('_production'));
  return key.includes('combined_soiling_ratio') ? 'Soiling Ratio' : key;
};

const cleaningPoints = (values: SeriesValue[] = []): SeriesValue[] =>
  values.map((value) => {
    if (value !== 0 && value !== null && value !== undefined) {
      // Always 1 high: the colour tells the cleaning kind apart.
      return { y: 1, color: (typeof value === 'number' && CLEANING_COLORS[value]) || '#b75cf8ff', borderWidth: 0 };
    }
    return value;
  });

const timeSeriesSeries = (data: TimeSeriesChartData): Highcharts.SeriesOptionsType[] => {
  const series: Highcharts.SeriesOptionsType[] = [];
  let i = 0;
  Object.keys(data.values).forEach((key) => {
    i = hasPlotLine(key) ? i : i + 1;
    if (key === 'cleaning_border') return;
    const isBar = key === 'rain' || key === 'cleaning';
    series.push({
      name: seriesName(key),
      data: (key === 'cleaning' ? cleaningPoints(data.values[key]) : data.values[key]) as Highcharts.PointOptionsType[],
      color: hasPlotLine(key) ? PLOT_LINES[key].color : colors[i],
      type: isBar ? 'column' : 'line',
      zIndex: isBar ? -10 : undefined,
      borderWidth: key === 'cleaning' ? 0 : undefined,
      pointPadding: key === 'cleaning' ? 0 : undefined,
      groupPadding: key === 'cleaning' ? 0 : undefined,
    } as Highcharts.SeriesOptionsType);
  });
  return series;
};

const rainPlotLines = (data: TimeSeriesChartData, timesMs: number[], datetimeAxis?: boolean): Highcharts.XAxisPlotLinesOptions[] => {
  if (!data.rain_dates?.length) return [];
  // First index of each day, as a findIndex over the times would give.
  const indexByDay = new Map<string, number>();
  data.times.forEach((t, idx) => {
    const ymd = dayjs(new Date(Date.parse(t))).format('YYYY-MM-DD');
    if (!indexByDay.has(ymd)) indexByDay.set(ymd, idx);
  });
  return data.rain_dates
    .map((val) => indexByDay.get(dayjs(val).format('YYYY-MM-DD')) ?? -1)
    .filter((idx) => idx >= 0)
    .map((idx) => ({
      label: { text: 'Rain', style: { fontSize: '10px' } },
      color: 'rgba(0, 0, 255, 0.7)',
      // On a datetime axis the plotLine value is a timestamp, otherwise the category index.
      value: datetimeAxis ? timesMs[idx] : idx,
      width: 2,
    }));
};

const timeSeriesXAxis = (data: TimeSeriesChartData, timesMs: number[], axis: TimeSeriesAxisOptions): Highcharts.XAxisOptions => {
  const plotLinesData = rainPlotLines(data, timesMs, axis.datetimeAxis);
  if (!axis.datetimeAxis) {
    return { categories: data.times.map(formatDay), title: { text: 'Date' }, gridLineWidth: 1, plotLines: plotLinesData };
  }
  return {
    type: 'datetime',
    title: { text: 'Date' },
    gridLineWidth: 1,
    plotLines: plotLinesData,
    min: timesMs[0],
    max: timesMs[timesMs.length - 1],
    ...(axis.tickInterval != null ? { tickInterval: axis.tickInterval } : {}),
    labels: {
      formatter: function () {
        const d = new Date(this.value);
        return pad2(d.getDate()) + '/' + pad2(d.getMonth() + 1) + '/' + d.getFullYear();
      },
    },
  };
};

/** Daily lines (soiling ratio, production…) with rain and cleaning bars; `null` until times and values are known. */
export const buildTimeSeriesOptions = (
  data: TimeSeriesChartData | null | undefined,
  axis: TimeSeriesAxisOptions,
): Highcharts.Options | null => {
  if (!data?.values || !data?.times) return null;
  const timesMs = data.times.map((x) => Date.parse(x));
  const pointInterval = timesMs.length > 1 ? timesMs[1] - timesMs[0] : DAY_MS;
  const isSoilingRatio = Object.keys(data.values).some((key) => key.includes('soiling_ratio'));

  return {
    chart: {
      type: 'line',
      zooming: { type: 'x' },
      ...(axis.marginLeft != null ? { marginLeft: axis.marginLeft } : {}),
      ...(axis.marginRight != null ? { marginRight: axis.marginRight } : {}),
    },
    // Timestamps are local midnights, so format ticks and tooltips in local time too
    ...(axis.datetimeAxis ? { time: { useUTC: false } } : {}),
    title: { text: data.title, align: 'left' },
    boost: { useGPUTranslations: true, usePreallocated: true },
    xAxis: timeSeriesXAxis(data, timesMs, axis),
    yAxis: [
      {
        title: { text: data.xTitle },
        ...(isSoilingRatio
          ? { min: axis.ymin, max: axis.ymax, tickInterval: 0.2, startOnTick: true, endOnTick: true, allowDecimals: true }
          : {}),
      },
    ],
    legend: { enabled: true },
    // Shared tooltips are slow with many series.
    tooltip: { valueDecimals: 4, shared: false, split: false, xDateFormat: '%d/%m/%Y' },
    credits: { enabled: false },
    plotOptions: {
      series: {
        // On a datetime axis, map each array index to its real timestamp.
        ...(axis.datetimeAxis ? { pointStart: timesMs[0], pointInterval } : {}),
        marker: { enabled: false, states: { hover: { enabled: true, radius: 3 } } },
        turboThreshold: 10000,
        animation: { duration: 500 },
      },
      line: { marker: { enabled: false }, lineWidth: 2 },
      area: { stacking: 'normal', marker: { enabled: false }, lineWidth: 2, lineColor: '#000000', fillOpacity: 0.7 },
      column: { pointPadding: 0, groupPadding: 0, borderWidth: 0, enableMouseTracking: true },
    },
    exporting: { csv: { dateFormat: '%Y-%m-%d' } },
    series: timeSeriesSeries(data),
  };
};
